import moment from './timezone';

/** Flat tip-share rates apply on/after this date (America/Chicago). */
export const TIP_FLAT_RATE_EFFECTIVE_DATE = '2026-08-01';

export const TIP_ROLES = {
  DEALER: 'dealer',
  WAITRESS: 'waitress',
  BARTENDER: 'bartender',
  FLOOR: 'floor',
};

const FLAT_RATES = {
  [TIP_ROLES.DEALER]: 0.10,
  [TIP_ROLES.WAITRESS]: 0.08,
  [TIP_ROLES.BARTENDER]: 0.05,
  [TIP_ROLES.FLOOR]: 0.05,
};

const LEGACY_FLAT_12 = 0.12;

const toChicagoDay = (transactionDate) => {
  if (!transactionDate) {
    return moment.tz('America/Chicago').startOf('day');
  }

  if (typeof transactionDate === 'string') {
    const datePart = transactionDate.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return moment.tz(datePart, 'YYYY-MM-DD', 'America/Chicago').startOf('day');
    }

    const parsed = moment.tz(
      transactionDate,
      ['MM/DD/YYYY hh:mm A', 'MM/DD/YYYY HH:mm', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DD HH:mm:ss'],
      true,
      'America/Chicago'
    );
    if (parsed.isValid()) {
      return parsed.startOf('day');
    }
  }

  return moment(transactionDate).tz('America/Chicago').startOf('day');
};

export const usesFlatTipRate = (transactionDate = new Date()) => {
  const txDate = toChicagoDay(transactionDate);
  const effectiveDate = moment
    .tz(TIP_FLAT_RATE_EFFECTIVE_DATE, 'YYYY-MM-DD', 'America/Chicago')
    .startOf('day');
  return txDate.isSameOrAfter(effectiveDate, 'day');
};

export const calculateSlabCommission = (amount) => {
  const amt = parseFloat(amount) || 0;
  if (amt <= 0) return 0;

  if (amt > 500) {
    return Math.round(450 * 0.12 + (amt - 500) * 0.20);
  }
  if (amt > 50) {
    return Math.round((amt - 50) * 0.12);
  }
  return 0;
};

export const calculateFlatRateCommission = (amount, rateOrRole) => {
  const amt = parseFloat(amount) || 0;
  if (amt <= 0) return 0;

  const rate =
    typeof rateOrRole === 'number' ? rateOrRole : FLAT_RATES[rateOrRole];
  if (rate == null) return calculateSlabCommission(amount);

  return Math.round(amt * rate);
};

export const getTipRoleFromEmployeeRole = (employeeRole) => {
  const role = String(employeeRole || '').trim().toLowerCase();

  if (role === 'dealer') return TIP_ROLES.DEALER;
  if (role === 'waitress') return TIP_ROLES.WAITRESS;
  if (role === 'bartender') return TIP_ROLES.BARTENDER;
  if (role === 'floor supervisor' || role === 'floor') return TIP_ROLES.FLOOR;

  return null;
};

export const calculateTipCommission = (amount, role, transactionDate = new Date()) => {
  if (usesFlatTipRate(transactionDate)) {
    const rate = FLAT_RATES[role];
    if (rate != null) {
      return calculateFlatRateCommission(amount, rate);
    }
    return calculateSlabCommission(amount);
  }

  if (role === TIP_ROLES.BARTENDER || role === TIP_ROLES.FLOOR) {
    return calculateFlatRateCommission(amount, LEGACY_FLAT_12);
  }

  return calculateSlabCommission(amount);
};

export const getTipCommissionLabel = (role, transactionDate = new Date()) => {
  if (usesFlatTipRate(transactionDate)) {
    const rate = FLAT_RATES[role];
    if (rate != null) {
      return `Tax (${Math.round(rate * 100)}%)`;
    }
    return 'Tax';
  }

  if (role === TIP_ROLES.BARTENDER || role === TIP_ROLES.FLOOR) {
    return 'Tax (12%)';
  }

  return 'Tax (slab)';
};

/** Backward-compatible helper for pages that only pass amount + role. */
export const createTipCommissionCalculator = (role) => (amount, transactionDate = new Date()) =>
  calculateTipCommission(amount, role, transactionDate);
